import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TableContainer from "Common/TableContainer";
import { Pencil, Search, Plus, Trash2 } from "lucide-react";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer } from "react-toastify";
import { Loader } from "Common/Loader";
import moment from "moment";
import { AddRack } from "./AddRack";
import { useRackList } from "./useRackList";
import { useRackModal } from "./useRackModal";
import { Common } from "helpers/common";

function RackList() {
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [eventData, setEventData] = useState<any>();
    const commonHelper = new Common();

    const {
        rackList,
        isLoading,
        handleDelete,
        updateRackList,
        filterSearchData
    } = useRackList();

    const {
        show,
        isEdit,
        rackData,
        toggle,
        handleEditDataClick,
        handleAddRack
    } = useRackModal((result) => {
        updateRackList(result, isEdit, rackData);
        toggle();
    });

    const deleteToggle = () => setDeleteModal(!deleteModal);

    const onClickDelete = (cell: any) => {
        setDeleteModal(true);
        if (cell.id) {
            setEventData(cell);
        }
    };

    const handleDeleteConfirm = async () => {
        if (eventData) {
            const success = await handleDelete(eventData.id);
            if (success) {
                setDeleteModal(false);
            }
        }
    };

    const columns = useMemo(() => [
        {
            header: "Name",
            accessorKey: "name",
            enableColumnFilter: true,
            cell: (cell: any) => (
                <div className="flex items-center gap-2">
                    <div className="grow">
                        <h6 className="mb-1 text-12">
                            {cell.getValue()}
                        </h6>
                    </div>
                </div>
            ),
        },
        {
            header: "Warehouse ID",
            accessorKey: "warehouse_id",
            enableColumnFilter: false,
            cell: (cell: any) => (
                <span className="text-slate-500 dark:text-zink-200">
                    {cell.getValue()}
                </span>
            ),
        },
        {
            header: "Created Date",
            accessorKey: "createdAt",
            enableColumnFilter: false,
            cell: (cell: any) => (
                <span className="text-slate-500 dark:text-zink-200">
                    {cell.getValue() ? moment(cell.getValue()).format("DD MMM, YYYY") : ""}
                </span>
            ),
        },
        {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: true,
            cell: (cell: any) => (
                <div className="flex gap-3">
                    <Link
                        to="#!"
                        data-modal-target="showModal"
                        className="flex items-center justify-center size-6 transition-all duration-200 ease-linear rounded-md edit-item-btn bg-slate-100 text-slate-500 hover:text-custom-500 hover:bg-custom-100 dark:bg-zink-600 dark:text-zink-200 dark:hover:bg-custom-500/20 dark:hover:text-custom-500"
                        onClick={() => {
                            const data = cell.row.original;
                            handleEditDataClick(data);
                        }}
                    >
                        <Pencil className="size-3" />
                    </Link>
                    <Link
                        to="#!"
                        className="flex items-center justify-center size-6 transition-all duration-200 ease-linear rounded-md remove-item-btn bg-red-100 text-red-500 hover:text-red-600 hover:bg-red-200 dark:bg-red-500/20 dark:text-red-500 dark:hover:bg-red-500/30 dark:hover:text-red-400"
                        onClick={() => {
                            const data = cell.row.original;
                            onClickDelete(data);
                        }}
                    >
                        <Trash2 className="size-3" />
                    </Link>
                </div>
            ),
        },
    ], []);

    return (
        <React.Fragment>
            <div style={{ marginTop: '10px' }}>
            <DeleteModal
                show={deleteModal}
                onHide={deleteToggle}
                onDelete={handleDeleteConfirm}
            />
            <ToastContainer closeButton={false} limit={1} />

            {isLoading ? (
                <Loader />
            ) : (
                <div className="card" id="rackTable">
                <div className="card-body">
                    <div className="flex items-center gap-3 mb-4">
                        <h6 className="text-12 grow">
                            Racks ({rackList.length})
                        </h6>
                        <div className="shrink-0">
                            <button
                                type="button"
                                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                                onClick={handleAddRack}
                            >
                                <Plus className="inline-block size-4" />{" "}
                                <span className="align-middle">Add Rack</span>
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <TableContainer
                            isPagination={true}
                            columns={columns || []}
                            data={rackList || []}
                            customPageSize={10}
                            divclassName="overflow-x-auto"
                            tableclassName="w-full whitespace-nowrap"
                            theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600"
                            thclassName="px-2.5 py-0.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                            tdclassName="px-2.5 py-0.5 border-y border-slate-200 dark:border-zink-500"
                            PaginationClassName="flex flex-col items-center mt-8 md:flex-row"
                        />
                    </div>
                </div>
            </div>
            )}

            <Modal
                show={show}
                onHide={toggle}
                id="defaultModal"
                modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600"
            >
                {rackData && <AddRack onSave={(result) => {
                    updateRackList(result, isEdit, rackData);
                    toggle();
                }} onHide={toggle} data={rackData} />}
            </Modal>

            <DeleteModal
                show={deleteModal}
                onHide={deleteToggle}
                onDelete={handleDeleteConfirm}
            />
            </div>
        </React.Fragment>
    );
}

export default RackList;